import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const TruncatedText = ({ text, lines = 2 }) => {
  const { t } = useTranslation();
  const wrapperRef = useRef(null);
  const measureRef = useRef(null);

  const [truncated, setTruncated] = useState(text);
  const [expanded, setExpanded] = useState(false);
  const [isOverflow, setIsOverflow] = useState(false);

  useEffect(() => {
    if (!wrapperRef.current || !measureRef.current || expanded) return;

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      const wrapper = wrapperRef.current;
      const measure = measureRef.current;

      if (!wrapper || !measure) return;

      // Copy width & font styles
      const styles = getComputedStyle(wrapper);
      measure.style.width = styles.width;
      measure.style.font = styles.font;
      measure.style.fontSize = styles.fontSize;
      measure.style.fontFamily = styles.fontFamily;
      measure.style.fontWeight = styles.fontWeight;
      measure.style.lineHeight = styles.lineHeight;
      measure.style.letterSpacing = styles.letterSpacing;
      measure.style.whiteSpace = "normal";
      measure.style.wordBreak = styles.wordBreak;

      const lineHeight = parseFloat(styles.lineHeight);
      const maxHeight = lineHeight * lines;

      // Check if text even needs truncation
      measure.textContent = text;
      if (measure.offsetHeight <= maxHeight) {
        setTruncated(text);
        setIsOverflow(false);
        return;
      }

      let low = 0;
      let high = text.length;
      let bestFit = text.length;

      // Binary search
      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        measure.textContent = text.slice(0, mid) + `… ${t("Show more")}`;

        if (measure.offsetHeight > maxHeight) {
          bestFit = mid - 1;
          high = mid - 1;
        } else {
          low = mid + 1;
        }
      }

      if (bestFit < text.length) {
        setTruncated(text.slice(0, Math.max(0, bestFit)) + "…");
        setIsOverflow(true);
      } else {
        setTruncated(text);
        setIsOverflow(false);
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [text, lines, expanded]);

  return (
    <span ref={wrapperRef} style={{ position: "relative" }}>
      {expanded ? text : truncated}
      {isOverflow && (
        <button
          type="button"
          className="show_more_btn"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setExpanded(!expanded);
          }}
        >
          {expanded ? ` ${t("Show less")}` : ` ${t("Show more")}`}
        </button>
      )}

      {/* Hidden measurer - always rendered */}
      <span
        ref={measureRef}
        aria-hidden="true"
        style={{
          position: "absolute",
          visibility: "hidden",
          top: 0,
          left: 0,
          zIndex: -1,
          whiteSpace: "normal",
          pointerEvents: "none",
        }}
      />
    </span>
  );
};

export default TruncatedText;
